// The GET /me probe decision. Only a 401 is a session verdict
// (v2/api-contract.md §GET /me); any other failure — a server error, a
// rejected fetch, an unreadable body — leaves the session unknown, so the
// caller offers a retry instead of signing the member out.

export type Impersonating = {
  actorName: string | null;
  targetName: string | null;
};

export type MeBody = {
  userId?: string;
  orgId: string | null;
  guest?: boolean;
  onboarding?: boolean;
  can_manage_platform?: boolean;
  can_impersonate?: boolean;
  impersonating?: Impersonating | null;
};

export type ProbedAuth =
  | { kind: 'unauthenticated' }
  | { kind: 'unavailable' }
  | { kind: 'guest'; orgId: string | null }
  | { kind: 'onboarding'; userId: string; orgId: string | null }
  | {
      kind: 'authenticated';
      userId: string;
      orgId: string | null;
      canManagePlatform: boolean;
      canImpersonate: boolean;
      impersonating: Impersonating | null;
    };

export const probeMe = async (
  fetchImpl: typeof fetch = fetch,
): Promise<ProbedAuth> => {
  try {
    const res = await fetchImpl('/me', { credentials: 'include' });
    if (res.status === 401) return { kind: 'unauthenticated' };
    if (res.status !== 200) return { kind: 'unavailable' };
    const data = (await res.json()) as MeBody | null;
    if (data === null || typeof data !== 'object') {
      return { kind: 'unavailable' };
    }
    // A guest session has no userId; route to the read-only guest surface.
    if (data.guest === true) {
      return { kind: 'guest', orgId: data.orgId ?? null };
    }
    // Every member shape needs a userId; without one the body is unreadable.
    if (typeof data.userId !== 'string') return { kind: 'unavailable' };
    if (data.onboarding === true) {
      return {
        kind: 'onboarding',
        userId: data.userId,
        orgId: data.orgId ?? null,
      };
    }
    return {
      kind: 'authenticated',
      userId: data.userId,
      orgId: data.orgId ?? null,
      canManagePlatform: data.can_manage_platform === true,
      canImpersonate: data.can_impersonate === true,
      impersonating: data.impersonating ?? null,
    };
  } catch {
    return { kind: 'unavailable' };
  }
};

// ---------------------------------------------------------------------------
// readAnswer — the one reader that turns an HTTP answer (or a rejected fetch)
// into its meaning (v2/api-contract.md §Errors). The body is read once.
// probeMe above keeps its own rule: only a 401 is a session verdict there.

export type Answer =
  | { kind: 'network' }
  | { kind: 'unauthorized' }
  | { kind: 'throttled' }
  | { kind: 'refused' }
  | { kind: 'unavailable' }
  | { kind: 'unreadable'; status: number }
  | { kind: 'ok'; status: number; body: unknown };

// Strings from v2/design-system.md §Network Error State.
export const NETWORK_ERROR_TEXT = 'שגיאת רשת';
export const ANSWER_TEXT = {
  throttled: 'רגע, נשלחו הרבה הודעות. נסו שוב בעוד רגע',
  refused: 'אין הרשאה לפעולה הזאת',
  unavailable: 'השרת לא זמין כרגע. נסו שוב',
} as const;

type ResponseLike = { status: number; text: () => Promise<string> };

const isResponseLike = (v: unknown): v is ResponseLike =>
  typeof v === 'object' &&
  v !== null &&
  typeof (v as ResponseLike).status === 'number' &&
  typeof (v as ResponseLike).text === 'function';

export const readAnswer = async (src: unknown): Promise<Answer> => {
  if (!isResponseLike(src)) return { kind: 'network' };
  const status = src.status;
  if (status === 401) return { kind: 'unauthorized' };
  if (status === 403) return { kind: 'refused' };
  if (status === 429) return { kind: 'throttled' };
  if (status >= 500) return { kind: 'unavailable' };
  if (status < 200 || status >= 300) return { kind: 'unreadable', status };
  try {
    return { kind: 'ok', status, body: JSON.parse(await src.text()) as unknown };
  } catch {
    return { kind: 'unreadable', status };
  }
};

// The bubble text for a failed answer; null for ok / unauthorized.
export const answerErrorText = (a: Answer): string | null => {
  switch (a.kind) {
    case 'network':
    case 'unreadable':
      return NETWORK_ERROR_TEXT;
    case 'throttled':
      return ANSWER_TEXT.throttled;
    case 'refused':
      return ANSWER_TEXT.refused;
    case 'unavailable':
      return ANSWER_TEXT.unavailable;
    default:
      return null;
  }
};

// A failed answer as the member meets it. Only `network` means the browser is
// offline (connected=false). `retry` is false for a refused (403) answer:
// asking again would be refused again.
export type Failure = { kind: 'error'; text: string; offline: boolean; retry: boolean };

const failure = (a: Answer): Failure => ({
  kind: 'error',
  text: answerErrorText(a) ?? NETWORK_ERROR_TEXT,
  offline: a.kind === 'network',
  retry: a.kind !== 'refused',
});

export type ChatOutcome =
  | { kind: 'accepted'; cid: string }
  | { kind: 'deduped' }
  | { kind: 'unauthorized' }
  | Failure;

// POST /chat accepts only 202 {cid:string} or 200 {deduped:true}.
export const chatOutcome = (a: Answer): ChatOutcome => {
  if (a.kind === 'unauthorized') return { kind: 'unauthorized' };
  if (a.kind === 'ok') {
    const b = a.body as { cid?: unknown; deduped?: unknown } | null;
    if (b && typeof b === 'object') {
      if (a.status === 202 && typeof b.cid === 'string' && b.cid) {
        return { kind: 'accepted', cid: b.cid };
      }
      if (a.status === 200 && b.deduped === true) return { kind: 'deduped' };
    }
    return failure({ kind: 'unreadable', status: a.status });
  }
  return failure(a);
};

export type HistoryOutcome =
  | { kind: 'loaded'; messages: unknown[] }
  | { kind: 'unauthorized' }
  | Failure;

// GET /chat/history: only 200 {messages: [...]} is a loaded history. A failed
// read is never shown as an empty one — the caller offers a retry.
export const historyOutcome = (a: Answer): HistoryOutcome => {
  if (a.kind === 'unauthorized') return { kind: 'unauthorized' };
  if (a.kind === 'ok') {
    const b = a.body as { messages?: unknown } | null;
    if (a.status === 200 && b && typeof b === 'object' && Array.isArray(b.messages)) {
      return { kind: 'loaded', messages: b.messages };
    }
    return failure({ kind: 'unreadable', status: a.status });
  }
  return failure(a);
};

export type LogoutOutcome = { kind: 'signed_out' } | Failure;

// POST /auth/logout: sign-out is complete only when the server confirms it —
// 200 {ok:true}, or a 401 (the server holds no session for this cookie). Any
// other answer keeps the member signed in, with the draft, and offers a retry.
export const logoutOutcome = (a: Answer): LogoutOutcome => {
  if (a.kind === 'unauthorized') return { kind: 'signed_out' };
  if (a.kind === 'ok') {
    const b = a.body as { ok?: unknown } | null;
    if (a.status === 200 && b && typeof b === 'object' && b.ok === true) {
      return { kind: 'signed_out' };
    }
    return failure({ kind: 'unreadable', status: a.status });
  }
  return failure(a);
};

export type RequestLinkOutcome =
  | { kind: 'sent'; pollNonce: string }
  | { kind: 'throttled' }
  | { kind: 'invalid' }
  | { kind: 'error'; text: string };

// POST /auth/request-link: only 200 {success, poll_nonce 43 chars} is "sent".
export const requestLinkOutcome = (a: Answer): RequestLinkOutcome => {
  if (a.kind === 'throttled') return { kind: 'throttled' };
  if (a.kind === 'unreadable' && a.status === 400) return { kind: 'invalid' };
  if (a.kind === 'ok') {
    const b = a.body as { success?: unknown; poll_nonce?: unknown } | null;
    if (
      a.status === 200 &&
      b &&
      typeof b === 'object' &&
      b.success === true &&
      typeof b.poll_nonce === 'string' &&
      b.poll_nonce.length === 43
    ) {
      return { kind: 'sent', pollNonce: b.poll_nonce };
    }
    return { kind: 'error', text: NETWORK_ERROR_TEXT };
  }
  return { kind: 'error', text: answerErrorText(a) ?? NETWORK_ERROR_TEXT };
};
